<script setup lang="ts" name="form-base-table">
import { computed, reactive, ref, watch } from 'vue'
import { useGlobalConfig } from '@pit-element-plus/components/config-provider'
import { formBaseTableEmits, formBaseTableProps } from './form-base-table'
import formBaseTableColumn from './form-base-table-column.vue'
import formBaseTableColumnVirtually from './form-base-table-column-virtually.vue'
import type { FormBaseColumns, FormBaseColumnsItem } from './form-base'

const props = defineProps(formBaseTableProps)
const emit = defineEmits(formBaseTableEmits)
const current = ref(null)
const dataTable = ref()

const defaultSort = ref({})
const orderArray: any = ref([])
const refreshTable = ref(true)

const tableHeadBackground = useGlobalConfig('tableHeadBackground')
const tableBackground = computed(() => {
  if (tableHeadBackground.value) {
    return { background: tableHeadBackground.value }
  }
  return {}
})

type StateType = {
  columns: FormBaseColumns
}

const state = reactive<StateType>({
  columns: [],
})

watch(
  () => props.columns,
  (val) => {
    Object.assign(state.columns, val)
  },
  {
    deep: true,
    immediate: true,
  }
)

const getColumnsExpand = computed(() => {
  return state.columns.filter((item) => item.expand)
})

const getColumns = computed(() => {
  return state.columns.filter((item) => !item.expand)
})

const handleHeaderCellClass = ({ column }: any) => {
  orderArray.value!.forEach((element: any) => {
    if (column.property === element.prop) {
      column.order = element.order
    }
  })
}

const handleSortChange = ({ prop, order }: any) => {
  if (order) {
    //参与排序
    // let flagIsHave = false;
    orderArray.value = orderArray.value!.filter(
      (item: any) => item.prop !== prop
    )
    // orderArray.value!.forEach((element: any) => {
    //   if (element.prop === prop) {
    //     element.order = order;
    //     flagIsHave = true;
    //   }
    // });
    orderArray.value!.push({
      prop,
      order,
    })
  } else {
    //不参与排序
    orderArray.value! = orderArray.value!.filter((element: any) => {
      return element.prop !== prop
    })
  }
  emit('table-sort-change', orderArray.value)
}

const handleCurrentChangePage = (val: number) => {
  emit('current-change', val)
}

const handleSizeChange = (val: number) => {
  emit('size-change', val)
}

const handleCurrentChange = (row: any) => {
  // todo 再次点击选中的取消
  if (!current.value) {
    current.value = row
  } else {
    current.value = null
  }
  if (props.isVirtuallyTable) {
    emit('table-current-change', row.row)
  } else {
    emit('table-current-change', row)
  }
}

const handleRowDblclick = (row: any) => {
  if (props.isVirtuallyTable) {
    emit('table-row-dblclick', row.row)
  } else {
    emit('table-row-dblclick', row)
  }
}

const handleSelectionChange = (row: any) => {
  emit('table-selection-change', row)
}

const handleClickInfo = (columns: FormBaseColumnsItem, row: any) => {
  emit('click-info', columns, row)
}

const tableLoadFn = (
  row: any,
  treeNode: unknown,
  resolve: (date: any[]) => void
) => {
  if (props.tableLoad) {
    props.tableLoad(row).then((res: any) => {
      resolve(res)
    })
  }
}

const doLayout = () => {
  dataTable.value!.doLayout()
}

const clearSelection = () => {
  dataTable.value!.clearSelection()
}

const setCurrentRow = (row: any) => {
  dataTable.value!.setCurrentRow(row)
}

const toggleRowSelection = (row: any, selected: boolean) => {
  dataTable.value!.toggleRowSelection(row, selected)
}

const setRefreshTable = (val: boolean) => {
  refreshTable.value = val
}

const dataTableRef = () => {
  return dataTable.value
}

defineExpose({
  setRefreshTable,
  doLayout,
  clearSelection,
  setCurrentRow,
  toggleRowSelection,
  dataTableRef,
})
</script>

<template>
  <div class="el-form-base-main">
    <div v-if="refreshTable" class="el-form-base-table">
      <slot name="tableWarp">
        <!-- 多选表格 -->
        <el-table
          v-if="props.rowSingle && !props.tableLazy && !props.isVirtuallyTable"
          ref="dataTable"
          :data="props.tableData"
          :border="true"
          :row-key="props.rowKey"
          :span-method="props.spanMethod"
          :default-expand-all="props.defaultExpandAll"
          :header-cell-style="tableBackground"
          :tree-props="{ children: 'children', hasChildren: 'hasChildren2' }"
          style="width: 100%"
          :default-sort="defaultSort"
          :header-cell-class-name="handleHeaderCellClass"
          :row-class-name="props.rowClassName"
          :cell-class-name="props.cellClassName"
          height="100%"
          @sort-change="handleSortChange"
          @selection-change="handleSelectionChange"
          @row-dblclick="handleRowDblclick"
        >
          <form-base-table-column
            v-for="(item, index) in getColumnsExpand"
            :key="index"
            :column="item as any"
            :columnsData="getColumnsExpand"
            @click-info="handleClickInfo"
          />
          <el-table-column
            type="selection"
            :align="props.selectionAlign"
            :width="props.selectionWidth"
            :fixed="props.selectionFixed"
          />
          <el-table-column
            v-if="props.noTableIndex"
            type="index"
            :align="props.indexAlign"
            :width="props.indexWidth"
            label="序号"
            :fixed="props.indexFixed"
          />
          <form-base-table-column
            v-for="(item, index) in getColumns"
            :key="index"
            :column="item as any"
            :columnsData="getColumns"
            @click-info="handleClickInfo"
          />
          <template #empty>
            <el-empty description="暂无数据" :image-size="120" />
          </template>
        </el-table>
        <!-- 单选表格 -->
        <el-table
          v-if="!props.rowSingle && !props.isVirtuallyTable && !props.tableLazy"
          ref="dataTable"
          :data="props.tableData"
          :border="true"
          :row-key="props.rowKey"
          :span-method="props.spanMethod"
          :default-expand-all="props.defaultExpandAll"
          highlight-current-row
          :header-cell-style="tableBackground"
          :tree-props="{ children: 'children', hasChildren: 'hasChildren2' }"
          style="width: 100%"
          :default-sort="defaultSort"
          :header-cell-class-name="handleHeaderCellClass"
          height="100%"
          :row-class-name="props.rowClassName"
          :cell-class-name="props.cellClassName"
          @sort-change="handleSortChange"
          @current-change="handleCurrentChange"
          @row-dblclick="handleRowDblclick"
        >
          <form-base-table-column
            v-for="(item, index) in getColumnsExpand"
            :key="index"
            :column="item as any"
            :columnsData="getColumnsExpand"
            @click-info="handleClickInfo"
          />
          <el-table-column
            v-if="props.noTableIndex"
            type="index"
            :align="props.indexAlign"
            :width="props.indexWidth"
            label="序号"
            :fixed="props.indexFixed"
          />
          <form-base-table-column
            v-for="(item, index) in getColumns"
            :key="index"
            :column="item as any"
            :columnsData="getColumns"
            @click-info="handleClickInfo"
          />
          <template #empty>
            <el-empty description="暂无数据" :image-size="120" />
          </template>
        </el-table>
        <!-- 单选懒加载表格 -->
        <el-table
          v-if="!props.rowSingle && props.tableLazy"
          ref="dataTable"
          :data="props.tableData"
          height="100%"
          :border="true"
          style="width: 100%"
          :row-key="props.rowKey"
          :span-method="props.spanMethod"
          :header-cell-style="tableBackground"
          highlight-current-row
          :lazy="props.tableLazy"
          :load="tableLoadFn"
          :default-sort="defaultSort"
          :header-cell-class-name="handleHeaderCellClass"
          :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
          :row-class-name="props.rowClassName"
          :cell-class-name="props.cellClassName"
          @sort-change="handleSortChange"
          @current-change="handleCurrentChange"
          @row-dblclick="handleRowDblclick"
        >
          <el-table-column
            v-if="props.noTableIndex"
            type="index"
            :align="props.indexAlign"
            :width="props.indexWidth"
            label="序号"
            :fixed="props.indexFixed"
          />
          <form-base-table-column
            v-for="item in state.columns"
            :key="item.prop"
            :column="item as any"
            :columnsData="state.columns"
            @click-info="handleClickInfo"
          />
          <template #empty>
            <el-empty description="暂无数据" :image-size="120" />
          </template>
        </el-table>
        <!-- 多选懒加载表格 -->
        <el-table
          v-if="props.rowSingle && props.tableLazy"
          ref="dataTable"
          :data="props.tableData"
          height="100%"
          :border="true"
          style="width: 100%"
          :row-key="props.rowKey"
          :span-method="props.spanMethod"
          :header-cell-style="tableBackground"
          highlight-current-row
          :lazy="props.tableLazy"
          :load="tableLoadFn"
          :default-sort="defaultSort"
          :header-cell-class-name="handleHeaderCellClass"
          :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
          :row-class-name="props.rowClassName"
          :cell-class-name="props.cellClassName"
          @sort-change="handleSortChange"
          @selection-change="handleSelectionChange"
          @row-dblclick="handleRowDblclick"
        >
          <el-table-column
            type="selection"
            :align="props.selectionAlign"
            :width="props.selectionWidth"
            :fixed="props.selectionFixed"
          />
          <el-table-column
            v-if="props.noTableIndex"
            type="index"
            :align="props.indexAlign"
            :width="props.indexWidth"
            label="序号"
            :fixed="props.indexFixed"
          />
          <form-base-table-column
            v-for="item in state.columns"
            :key="item.prop"
            :column="item as any"
            :columnsData="state.columns"
            @click-info="handleClickInfo"
          />
          <template #empty>
            <el-empty description="暂无数据" :image-size="120" />
          </template>
        </el-table>
        <!-- 单选虚拟化表格 -->
        <VxeTable
          v-if="!props.rowSingle && !props.tableLazy && props.isVirtuallyTable"
          ref="dataTable"
          show-overflow
          border
          height="100%"
          :row-id="rowKey"
          :row-config="{
            isHover: true,
            isCurrent: true,
            keyField: rowKey,
          }"
          :column-config="{ resizable: true }"
          :tree-config="{
            transform: true,
            rowField: rowKey,
            parentField: 'parentId',
            expandAll: props.defaultExpandAll,
          }"
          :scroll-y="{ gt: 20 }"
          :data="props.tableData"
          @cell-dblclick="handleRowDblclick"
          @cell-click="handleCurrentChange"
        >
          <VxeColumn
            v-if="props.noTableIndex"
            type="seq"
            title="序号"
            align="center"
            :width="props.indexWidth"
          />
          <form-base-table-column-virtually
            v-for="item in state.columns"
            :key="item.prop"
            :column="item as any"
            :columnsData="state.columns"
            @click-info="handleClickInfo"
          />
        </VxeTable>
        <!-- 多选虚拟化表格 -->
        <div
          v-if="props.rowSingle && !props.tableLazy && props.isVirtuallyTable"
        >
          单选虚拟化表格
        </div>
      </slot>
    </div>
    <div v-if="props.noPagination" class="el-form-base-pagination">
      <div class="el-form-base-pagination-right">
        <el-pagination
          background
          :current-page="props.pagination.pageNumber"
          :page-sizes="[10, 20, 30, 50, 100]"
          :page-size="props.pagination.pageSize"
          layout="total, sizes, prev, pager, next"
          :total="props.pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChangePage"
        />
      </div>
    </div>
  </div>
</template>
